<script setup>
import { ref, computed, watch } from 'vue'
import { useAppStore } from '@/stores/app'
import { useDataStore } from '@/stores/data'
import FloatingDialog from './FloatingDialog.vue'

const appStore = useAppStore()
const dataStore = useDataStore()

const helpAvailable = computed(() => {
	if (appStore.currentPage == 'trips') {
		if (appStore.editStep == '') return true
		if (appStore.editStep == 'change') return true
		if (appStore.editStep == 'splitTrip') return true
		if (appStore.editStep == 'splitTripActive') return true
		if (appStore.editStep == 'joinTrips') return true
		if (appStore.editStep == 'deleteTrip') return true
		if (appStore.editStep == 'joinPrevJourney') return true
		if (appStore.editStep == 'joinNextJourney') return true
		if (appStore.editStep == 'splitJourney') return true
		if (appStore.editStep == 'addTrip') return true
		if (appStore.editStep == 'surveyTrip') return true
	}
	if (appStore.currentPage == 'approve') {
		return true
	}
	return false
})

const currentTitle = computed(() => {
	if (appStore.currentPage == 'approve') {
		return 'Hjelp for reisemål og formål'
	}
	if (appStore.currentPage == 'trips') {
		if (appStore.editStep == '') return 'Hjelp for verifisering av reise'
		if (appStore.editStep == 'change') return 'Hjelp for redigering av reise'
		if (appStore.editStep == 'splitTrip') return 'Hjelp for oppdeling av etappe'
		if (appStore.editStep == 'splitTripActive') return 'Hjelp for oppdeling av etappe'
		if (appStore.editStep == 'joinTrips') return 'Hjelp for sammenslåing av etapper'
		if (appStore.editStep == 'deleteTrip') return 'Hjelp for sletting av etappe'
		if (appStore.editStep == 'joinPrevJourney' || appStore.editStep == 'joinNextJourney') return 'Hjelp for sammenslåing av hele reiser'
		if (appStore.editStep == 'splitJourney') return 'Hjelp for oppdeling av reise'
		if (appStore.editStep == 'addTrip') return 'Hjelp for å legge til etappe'
		if (appStore.editStep == 'surveyTrip') return 'Hjelp for spesifisering av etappe'
	}
	return 'Hjelp'
})
</script>

<template>
	<FloatingDialog icon="fa-solid fa-question" :disabled="!helpAvailable" v-model:expanded="appStore.expandedHelp" :title="currentTitle" position="top-right" button-class="fixed bigshadow roundButton text-gray-900">
		<template v-if="appStore.currentPage == 'approve'">
			<div class="text">
				<p>Selv om vi har registrert posisjonen for reisens destinasjon trenger vi at du også bekrefter det endelige reisemålet.</p>
				<p>Du kan velge fra listen over interessepunkter i nærområdet, eller du kan søke opp et interessepunkt eller en adresse.</p>
				<p>Vi trenger også at du angir formålet for reisen. Når du velger reisemål vil appen forsøke å forhåndsutfylle dette basert på type interessepunkt, men du bør endre på dette om det ikke passer med faktisk formål.</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == ''">
			<div class="text">
				<p>Denne oversikten representer en reise du har hatt, og hvilke etapper den består av fordelt på transportmiddel.</p>
				<p>Forsøk å avgjør om estimatet for reise og etapper er korrekt. Du kan zoome og panorerer i kartet eller trykke på en etappe for se den nærmere i kartet.</p>
				<p>
					Dersom noe er galt eller mangler kan du gjøre endringer ved å trykke på <span class="ghostbutton"><i class="fas fa-edit"></i>Rediger</span>. Dersom alt ser riktig ut kan du fortsette.
				</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == 'change'">
			<div class="text">
				<p>
					I redigeringsmodus kan trykke på <span class="ghostbutton round"><i class="fas fa-edit"></i></span> knappen ved siden av en etappe, dersom du vil endre transportmiddel, avreise- eller ankomststed, eller tidspunkter for en etappe.
				</p>
				<p>Bruk knappene under dersom reisen eller etapper er delt opp feil, dersom det mangler en etappe, eller om du vil fjerne etappe som ikke stemmer.</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == 'splitTrip'">
			<div class="text">
				<p>Når en etappe egentlig skulle vært delt opp i flere etapper med forskjellig transportmiddel, kan du velge å dele opp etappen.</p>
				<p>
					Begynn med å angi hvilken etappe du ønsker å dele opp og så trykke <span class="ghostbutton"><i class="fas fa-down-left-and-up-right-to-center"></i>Del opp etappen</span> for å starte prosessen. Deretter kan du angi posisjon på kartet der etappen skal deles opp
				</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == 'splitTripActive'">
			<div class="text">
				<p>Når en etappe egentlig skulle vært delt opp i flere etapper med forskjellig transportmiddel, kan du velge å dele opp etappen.</p>
				<p>
					Dra i skyveknappen for å spore dine bevegelser i kartet. Når du finner punktet der etappen skal avsluttes, der den nye etappen vil begynne, trykker du på <span class="ghostbutton"><i class="fas fa-down-left-and-up-right-to-center"></i>Del opp etappen her</span>
				</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == 'joinTrips'">
			<div class="text">
				<p>Når to eller flere etapper egentlig skulle vært samme etappe med samme transportmiddel, kan du velge å slå sammen etapper.</p>
				<p>
					Huk av <span class="ghostbutton round"><i class="fas fa-check"></i></span> etapper som skal slås sammen til èn etappe, og trykk så på <span class="ghostbutton"><i class="fas fa-down-left-and-up-right-to-center"></i>Slå sammen</span>.
				</p>
				<p>Dersom du trenger å endre hvilket transportmiddel som ble brukt for den sammenslåtte etappen, kan du gjøre dette etterpå ved å redigere etappen.</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == 'deleteTrip'">
			<div class="text">
				<p>Dersom en etappe ikke stemmer, og det er vanskelig å korrigere den ved å redigere den, kan du velge å slette etappen.</p>
				<p>
					Velge etappen <span class="ghostbutton round"><i class="fas fa-check"></i></span> som skal slettes, og trykk så på <span class="ghostbutton"><i class="fas fa-down-left-and-up-right-to-center"></i>Slett etappen</span> for å merke den som slettet.
				</p>
				<p>
					Dersom du sletter feil etappe, kan du etterpå gjenta prosessen for å angre <span class="ghostbutton round"><i class="fas fa-trash-can-arrow-up"></i></span> slettingen.
				</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && (appStore.editStep == 'joinPrevJourney' || appStore.editStep == 'joinNextJourney')">
			<div class="text">
				<p>Dersom to eller flere påfølgende reiser egentlig hører sammen, fordi de har samme formål, f.eks en reise til arbeidssted, kan du slå de sammen.</p>
				<p>Du har nå valgt å slå sammen gjeldende reise med <strong v-if="appStore.editStep == 'joinNextJourney'">neste reise</strong><strong v-if="appStore.editStep == 'joinPrevJourney'">forrige reise</strong>. Oversikten under viser alle etappen fra begge reiser og hvordan de vil bli slått sammen.</p>
				<p>
					Dersom dette ser riktig ut trykker du på <span class="ghostbutton"><i class="fas fa-save"></i>Slå sammen</span> for å utføre sammenslåingen.
				</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == 'splitJourney'">
			<div class="text">
				<p>Dersom en reise egentlig skulle vært delt opp i to reiser, med forskjellig formål, kan du velge å dele opp reisen. F.eks om du på vei hjem fra jobb har stoppet for å handle, og det ikke har blitt fanget opp.</p>
				<p>
					Trykk på <span class="ghostbutton round"><i class="fas fa-cut"></i></span> saksen for å anngi hvor reisen skal deles opp. Deretter trykker du på <span class="ghostbutton"><i class="fas fa-up-right-and-down-left-from-center"></i>Del opp reisen</span> for å utføre oppdelingen.
				</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == 'addTrip'">
			<div class="text">
				<p>Dersom en reise mangler en etappe, kan du velge å legge til etappen som mangler.</p>
				<p>
					Begynn med å anngi hviken etappe <span class="ghostbutton round"><i class="fas fa-check"></i></span> den nye etappen skal følges etter. Trykk så på <span class="ghostbutton"><i class="fas fa-plus"></i>Legg til etappe</span> for å opprette en ny etappe.
				</p>
				<p>Etterpå kan du angi detaljer for den nye etappen, som transportmiddel, avreise- og ankomststed og tidspunkter for dette.</p>
			</div>
		</template>

		<template v-if="appStore.currentPage == 'trips' && appStore.editStep == 'surveyTrip'">
			<div class="text">
				<p>Avhengig av fremkomstmiddel for etappene trenger vi i noen tilfeller litt ekstra informasjon om frekomstmiddelet, før du kan fortsette.</p>
			</div>
		</template>
	</FloatingDialog>
</template>

<style scoped>
.text .fa-solid {
	float: left;
	margin-right: 1rem;
	font-size: 2rem;
}

.text p {
	margin: 0px;
	margin-bottom: 0.5rem;
}

.text p:last-child {
	margin-bottom: 0rem;
}
</style>
